import { useMemo, useState } from "react";

// Interfaces
import { ICommissionCycle, IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";

// Material
import Grid from "@mui/material/Grid";

// Layouts
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";

// Components
import CommissionCycleAdjustment from "@/Washouts/components/CommissionCycle/CommissionCycleAdjustment";
import CommissionCycleClosingSection from "@/Washouts/components/CommissionCycle/CommissionCycleClosingSection";
import CommissionCyclePayroll from "@/Washouts/components/CommissionCycle/CommissionCyclePayroll";
import CommissionCyclePayrollSubmissionHistory from "@/Washouts/components/CommissionCycle/CommissionCyclePayrollSubmissionHistory";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

const getPrioritizedPosition = (positionStatusPartOne: IStatus, positionStatusPartTwo: IStatus) => {
	const PRIORITY_ORDER = ["PENDING", "REVIEWING", "APPROVED", "POSTED_PAYROLL", "UNREVIEWABLE"];

	const statusPartOne = PRIORITY_ORDER.indexOf(positionStatusPartOne.status);
	const statusPartTwo = PRIORITY_ORDER.indexOf(positionStatusPartTwo.status);

	return statusPartOne < statusPartTwo ? positionStatusPartOne : positionStatusPartTwo;
};

const buildTotalOfPositionStatus = (positionsPartOne: IStatus[], positionsPartTwo: IStatus[]) => {
	const totalPositions = [...positionsPartOne, ...positionsPartTwo];

	// Remove duplicates by prioritized status
	return totalPositions.reduce((acc, position) => {
		const index = acc.findIndex((item) => item.position === position.position);
		if (index === -1) {
			return [...acc, position];
		}

		const prioritizedPosition = getPrioritizedPosition(acc[index], position);
		acc[index] = prioritizedPosition;

		return acc;
	}, [] as IStatus[]);
};

type PayrollClosingContainerProps = {
	selectedItem: IPayrollClosingSideBarSelected | null;
	selection: boolean;
	locationId?: string;
	current: boolean;
	commissionCyclesLoading?: boolean;
	commissionCycle?: ICommissionCycle;
	payPeriod?: IPayPeriod;
	canCreateAdjustments: boolean;
	partnerPayPeriod?: IPayPeriod | null;
	positionStatusPayPeriodParts?: IStatus[];
};

export default function PayrollClosingContainer(props: Readonly<PayrollClosingContainerProps>) {
	const {
		locationId = "",
		selectedItem,
		selection,
		current,
		commissionCyclesLoading,
		commissionCycle,
		payPeriod,
		canCreateAdjustments,
		partnerPayPeriod = null,
		positionStatusPayPeriodParts = [],
	} = props;

	const [hasAdjustments, setHasAdjustments] = useState(true);

	const positionsStatus = useMemo(() => {
		if (!selectedItem || !payPeriod) {
			return [];
		}

		if (!partnerPayPeriod) {
			return payPeriod.positionStatus;
		}

		return buildTotalOfPositionStatus(payPeriod.positionStatus, partnerPayPeriod?.positionStatus ?? []);
	}, [selectedItem, payPeriod, partnerPayPeriod]);

	return (
		<>
			{selectedItem && (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<RoundedBox className="!py-7 !px-8">
							<CommissionCycleClosingSection
								selectedItem={selectedItem}
								locationId={locationId}
								commissionCyclesLoading={commissionCyclesLoading}
								commissionCycle={commissionCycle}
								payPeriod={payPeriod}
							/>
						</RoundedBox>
					</Grid>
					<Grid item xs={12}>
						<RoundedBox className="!py-7 !px-8">
							<CommissionCyclePayroll
								selectedItem={selectedItem}
								selection={selection}
								locationId={locationId}
								positionStatus={positionsStatus}
								commissionCycle={commissionCycle}
								payPeriod={payPeriod}
								partnerPayPeriod={partnerPayPeriod}
								positionStatusPayPeriodParts={positionStatusPayPeriodParts}
							/>
						</RoundedBox>
					</Grid>
					<Grid item xs={12}>
						<RoundedBox className="!py-7 !px-8">
							<CommissionCyclePayrollSubmissionHistory
								selectedItem={selectedItem}
								commissionCycle={commissionCycle}
								payPeriod={payPeriod}
								selection={selection}
								locationId={locationId}
								current={current}
							/>
						</RoundedBox>
					</Grid>
					<Grid item xs={12} display={canCreateAdjustments || hasAdjustments ? "block" : "none"}>
						<RoundedBox className="!py-7 !px-8">
							<CommissionCycleAdjustment
								selection={selection}
								locationId={locationId}
								canCreateAdjustments={canCreateAdjustments}
								payPeriodId={selectedItem?.payPeriodId ?? ""}
								positionStatus={positionsStatus}
								onHasAdjustments={setHasAdjustments}
							/>
						</RoundedBox>
					</Grid>
				</Grid>
			)}
			{!selectedItem && (
				<RoundedBox>
					<EmptyBody
						image={selectedItem ? emptyTable : empty}
						imageStyles={{ width: "340px" }}
						title={"No item selected"}
						bodyHeight={"510px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				</RoundedBox>
			)}
		</>
	);
}
